import { SortTableColumnDef } from '@shared/sort-table/sort-table.model'
import { ApplicationError } from './../../error/model/error'
import {
  CreditGrossLossScenario,
  CreditPortfolio,
  PortfolioType,
  ScenarioType,
} from './../../credit/model/credit-portfolio.model'
import { CreditAdminCalculationStructure } from './../../credit/model/credit-structure.model'
import {
  CreditSubmissionStatus,
  CreditSubmissionStructure,
} from '../../credit/model/credit-submission.model'
import { EnrichedModelRunLogs, RunStatus } from '../../model-runner/model/model-runner.model'

export const CREDIT_ADMIN_PERMISSION = 'Credit Security'

export enum CreditAdminTab {
  Portfolios = 'Portfolios',
  Programs = 'Programs',
  Structures = 'Structures',
  Submissions = 'Submissions',
}

export const CREDIT_ADMIN_TABS: CreditAdminTab[] = [
  CreditAdminTab.Portfolios,
  CreditAdminTab.Programs,
  CreditAdminTab.Structures,
  CreditAdminTab.Submissions,
]

export const DELETABLE_SUBMISSION_STATUSES: CreditSubmissionStatus[] = [
  CreditSubmissionStatus.Draft,
  CreditSubmissionStatus.Final,
  CreditSubmissionStatus.Approved,
]

export type CreditAdminTabConfigs = Record<CreditAdminTab, CreditAdminTabConfig<RowType>>

export type RowType =
  CreditPortfolio | CreditAdminProgram | CreditAdminCalculationStructure | CreditSubmissionStructure

export type CreditAdminTabConfig<T> = {
  rows: T[]
  columns: SortTableColumnDef[]
  rowSelected: boolean
}

export enum CreditAdminPortfolioDialogTab {
  PortfolioDetails = 'Portfolio Details',
  GrossLossScenarios = 'Gross Loss Scenarios',
  ImportModeling = 'Import Modeling',
}

export const CREDIT_ADMIN_PORTFOLIO_DIALOG_TABS: CreditAdminPortfolioDialogTab[] = [
  CreditAdminPortfolioDialogTab.PortfolioDetails,
  CreditAdminPortfolioDialogTab.GrossLossScenarios,
  CreditAdminPortfolioDialogTab.ImportModeling,
]

export enum CreditFileType {
  PortfolioData = 'portfolio',
  GsePortfolioData = 'gse_portfolio',
  ActualGrossLossScenarioData = 'actuals_gls', // for actual
  GrossLossScenarioData = 'gls', // for MI origination
  GseGrossLossScenarioData = 'gse_gls', // for GSE origination
  ActualLoanGrossLossScenarioData = 'actuals_loan', // for actual
  LoanGrossLossScenarioData = 'loan', // for origination
  GseLoanGrossLossScenarioData = 'gse_loan', // for GSE origination
  PAttachData = 'pattach', // for default gross loss scenario
  GsePAttachData = 'gse_pattach', // for GSE default gross loss scenario
  StochasticData = 'stochastic', // for MI simulated scenarios
  GseStochasticData = 'gse_stochastic', // for GSE simulated scenarios
}

export interface CreditCededFileProps {
  structureId: string
  transactionId: string
  scenarioMappingId?: string // MI only; not set for GSE (uses grossLossScenarioId + returnPeriod)
  trancheId?: string
  grossLossScenarioId?: string // GSE: GLS _id
  returnPeriod?: string // GSE stochastic: return period string
}

export type CreditPortfolioUploadResponse = {
  data?: CreditPortfolioUploadResults
  error?: ApplicationError
}

export interface CreditPortfolioUploadResults {
  message: CreditPortfolioUploadMessage[]
  date?: string
}

interface CreditPortfolioUploadMessage {
  file_size: number
  href: string
  return_period: number
  filename: string
  model_scenario_mapping_id?: number
}

const CREDIT_PORTFOLIO_KEYS: Record<string, keyof CreditPortfolio> = {
  Id: '_id',
  Name: 'name',
  AssetClass: 'asset_class',
  PortfolioType: 'data_type',
  ExposureBase: 'exposure_base',
  TotalExposure: 'total_exposure',
  ExpenseRatio: 'expense_ratio',
  StartBusinessCoveredDate: 'start_business_covered_date',
  EndBusinessCoveredDate: 'end_business_covered_date',
} as const

enum CreditPortfolioLabel {
  Id = 'ID',
  Name = 'Portfolio',
  AssetClass = 'Asset Class',
  PortfolioType = 'Portfolio Type',
  ExposureBase = 'Exposure Base',
  TotalExposure = 'Total Exposure',
  ExpenseRatio = 'Expense Ratio',
  StartBusinessCoveredDate = 'Start Business Covered Date',
  EndBusinessCoveredDate = 'End Business Covered Date',
}

export const CREDIT_ADMIN_PORTFOLIO_COLUMNS: SortTableColumnDef[] = [
  {
    id: CREDIT_PORTFOLIO_KEYS.Id,
    label: CreditPortfolioLabel.Id,
    valueType: 'text',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.Name,
    label: CreditPortfolioLabel.Name,
    valueType: 'text',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.AssetClass,
    label: CreditPortfolioLabel.AssetClass,
    valueType: 'text',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.PortfolioType,
    label: CreditPortfolioLabel.PortfolioType,
    valueType: 'text',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.ExposureBase,
    label: CreditPortfolioLabel.ExposureBase,
    valueType: 'text',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.TotalExposure,
    label: CreditPortfolioLabel.TotalExposure,
    valueType: 'short-numeric',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.ExpenseRatio,
    label: CreditPortfolioLabel.ExpenseRatio,
    valueType: 'percentage',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.StartBusinessCoveredDate,
    label: CreditPortfolioLabel.StartBusinessCoveredDate,
    valueType: 'date',
  },
  {
    id: CREDIT_PORTFOLIO_KEYS.EndBusinessCoveredDate,
    label: CreditPortfolioLabel.EndBusinessCoveredDate,
    valueType: 'date',
  },
]

export const TEMP_PORTFOLIO_ID = 'TEMP_PORTFOLIO_ID'

export const DEFAULT_PORTFOLIO: CreditPortfolio = {
  _id: TEMP_PORTFOLIO_ID,
  name: 'Unlabeled Portfolio',
  metadata: {},
  asset_class: 'residential mortgage',
  data_type: PortfolioType.MI,
  exposure_base: 'RIF',
  total_exposure: 13329668418,
  expense_ratio: 0.23,
  portfolio_data: '',
  start_business_covered_date: '2024-01-01T00:00:00',
  end_business_covered_date: '2024-12-31T00:00:00',
  portfolio_as_of_date: '2024-01-01T00:00:00',
  modified_date: '',
  gross_loss_scenarios: [],
  actuals: [],
  portfolio_results: {},
}

const DEFAULT_GROSS_LOSS_SCENARIO: CreditGrossLossScenario = {
  name: 'Milliman Unlabled',
  metadata: {},
  scenario: 'Milliman Unlabeled',
  model: 'Milliman Simulations',
  gross_loss_scenario_data: '',
  loan_gross_loss_scenario_data: '',
  pattach_data: '',
  scenario_type: ScenarioType.Deterministic,
  stochastic_data: null,
  actuals: [],
  is_default: false,
  modified_date: '',
}

export interface CreditAdminProgram {
  id: number
  name: string
  description: string
  portfolioId: string
  clientId: number
  clientName: string
  clientYearId: number
  clientYear: number
}

const CREDIT_PROGRAM_KEYS: Record<string, keyof CreditAdminProgram> = {
  Id: 'id',
  Name: 'name',
  Description: 'description',
  PortfolioId: 'portfolioId',
  ClientId: 'clientId',
  ClientName: 'clientName',
  ClientYearId: 'clientYearId',
  ClientYear: 'clientYear',
} as const

enum CreditProgramLabel {
  Id = 'ID',
  Name = 'Program',
  Description = 'Description',
  PortfolioId = 'Portfolio ID',
  ClientId = 'Client ID',
  ClientName = 'Client',
  ClientYearId = 'Client Year ID',
  ClientYear = 'Year',
}

export const CREDIT_ADMIN_PROGRAM_COLUMNS: SortTableColumnDef[] = [
  {
    id: CREDIT_PROGRAM_KEYS.Id,
    label: CreditProgramLabel.Id,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.Name,
    label: CreditProgramLabel.Name,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.Description,
    label: CreditProgramLabel.Description,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.PortfolioId,
    label: CreditProgramLabel.PortfolioId,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.ClientId,
    label: CreditProgramLabel.ClientId,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.ClientName,
    label: CreditProgramLabel.ClientName,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.ClientYearId,
    label: CreditProgramLabel.ClientYearId,
    valueType: 'text',
  },
  {
    id: CREDIT_PROGRAM_KEYS.ClientYear,
    label: CreditProgramLabel.ClientYear,
    valueType: 'text',
  },
]

export const CREDIT_ADMIN_STRUCTURE_COLUMNS: SortTableColumnDef[] = [
  {
    id: 'id',
    label: 'ID',
    valueType: 'text',
  },
  {
    id: 'name',
    label: 'Structure',
    valueType: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    valueType: 'text',
  },
  {
    id: 'program',
    label: 'Program',
    valueType: 'text',
  },
  {
    id: 'year',
    label: 'Year',
    valueType: 'text',
  },
  {
    id: 'carrier',
    label: 'Client',
    valueType: 'text',
  },
  {
    id: 'updated',
    label: 'Last Updated',
    valueType: 'date',
  },
  {
    id: 'creator',
    label: 'Creator',
    valueType: 'text',
  },
  {
    id: 'created',
    label: 'Created',
    valueType: 'date',
  },
  {
    id: 'lastUpdater',
    label: 'Last Updated By',
    valueType: 'text',
  },
]

export const CREDIT_ADMIN_SUBMISSION_COLUMNS: SortTableColumnDef[] = [
  {
    id: 'id',
    label: 'ID',
    valueType: 'text',
  },
  {
    id: 'label',
    label: 'Submission',
    valueType: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    valueType: 'text',
  },
  {
    id: 'status',
    label: 'Status',
    valueType: 'text',
  },
  {
    id: 'audit_insert_dt',
    label: 'Created',
    valueType: 'date',
  },
]

export const CREDIT_ADMIN_TAB_CONFIGS: CreditAdminTabConfigs = {
  [CreditAdminTab.Portfolios]: {
    rows: [],
    columns: CREDIT_ADMIN_PORTFOLIO_COLUMNS,
    rowSelected: false,
  },
  [CreditAdminTab.Programs]: {
    rows: [],
    columns: CREDIT_ADMIN_PROGRAM_COLUMNS,
    rowSelected: false,
  },
  [CreditAdminTab.Structures]: {
    rows: [],
    columns: CREDIT_ADMIN_STRUCTURE_COLUMNS,
    rowSelected: false,
  },
  [CreditAdminTab.Submissions]: {
    rows: [],
    columns: CREDIT_ADMIN_SUBMISSION_COLUMNS,
    rowSelected: false,
  },
}

/**
 * Represents a run name group within a scenario
 * For LEAPS: Contains one run with both outputs
 * For Milliman: Contains separate scenarioRun and loanRun
 */
export interface RunNameGroup {
  runName: string
  runDescription: string
  portfolioName: string
  user: string
  timestamp: string
  duration: number | null
  scenarioRun?: EnrichedModelRunLogs // resultType === 'scenario' (or the single run for LEAPS)
  loanRun?: EnrichedModelRunLogs // resultType === 'loan' (Milliman only)
}

/**
 * Represents a group of runs for a specific scenario mapping
 * Used in the credit admin import flow to allow users to select which run to import
 * Runs are grouped by runName to handle Milliman's dual-run structure
 */
export interface ScenarioRunGroup {
  scenarioMappingId: number
  scenarioMappingName: string
  modelName: string
  scenarioName: string
  isStochastic: boolean
  runNameGroups: RunNameGroup[] // Grouped by runName
  selectedRunName: string | null // User selects one runName per scenario
}
